import React, { useState, useEffect, useContext } from "react";
import { Form, Input, Button, notification } from "antd";
import { auth, rtdb } from "../../../../firebase.config";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { ref as dbRef, update } from "firebase/database";
import { UserContext } from "../../../../context/UserContext";

const PhoneVerificationPage = () => {
  const [form] = Form.useForm();
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [api, contextHolder] = notification.useNotification();
  const navigate = useNavigate();
  const { user, setUser } = useContext(UserContext);

  // Reset reCAPTCHA each time page loads
  useEffect(() => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        {
          size: "invisible",
          callback: () => console.log("Recaptcha solved!"),
        }
      );

      window.recaptchaVerifier.render().then((widgetId) => {
        window.recaptchaWidgetId = widgetId;
      });
    }
  }, []);

  const handleSendCode = async ({ phone }) => {
    setLoading(true);
    try {
      // Ensure +1 prefix is enforced
      let formattedPhone = phone.startsWith("+1")
        ? phone
        : `+1${phone.replace(/\D/g, "")}`;

      console.log("Formatted phone:", formattedPhone);

      const confirmation = await signInWithPhoneNumber(
        auth,
        formattedPhone,
        window.recaptchaVerifier
      );

      setConfirmationResult(confirmation);
      api.success({ message: "Verification code sent!" });
    } catch (err) {
      console.error("Error during signInWithPhoneNumber:", err);
      let msg = "Something went wrong.";
      switch (err.code) {
        case "auth/invalid-phone-number":
          msg = "Invalid phone number format. Use +1XXXXXXXXXX.";
          break;
        case "auth/too-many-requests":
          msg = "Too many requests. Try again later.";
          break;
        case "auth/argument-error":
          msg = "Missing/invalid arguments in signInWithPhoneNumber.";
          break;
        case "auth/invalid-app-credential":
          msg = "Invalid app credential. Please refresh and try again.";
          break;
        default:
          msg = err.message || msg;
      }
      api.error({ message: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async ({ code }) => {
    setLoading(true);
    try {
      if (!confirmationResult) throw new Error("No verification ID available");

      await confirmationResult.confirm(code);

      // Update Realtime DB (use displayPhone + phoneVerified)
      const userRef = dbRef(rtdb, `landlords/${user.uid}`);
      await update(userRef, {
        displayPhone: user.displayPhone,
        phoneVerified: true,
      });

      // Update UserContext
      setUser({
        ...user,
        phoneVerified: true,
      });

      api.success({ message: "Phone verified!" });
      navigate("/landlord/dashboard");
    } catch (err) {
      console.error("Error verifying code:", err);
      api.error({ message: "Invalid code. Try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div id="recaptcha-container" />
      <div
        style={{
          maxWidth: 360,
          margin: "0 auto",
          paddingTop: 20,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {!confirmationResult ? (
          <Form layout="vertical" onFinish={handleSendCode} form={form}>
            <Form.Item
              name="phone"
              initialValue={user?.displayPhone || ""}
              rules={[{ required: true, message: "Enter phone number" }]}
            >
              <Input placeholder="Enter phone (auto +1 added)" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block>
                Send Code
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <Form layout="vertical" onFinish={handleVerifyCode}>
            <Form.Item
              name="code"
              rules={[{ required: true, message: "Enter 6-digit code" }]}
            >
              <Input placeholder="Enter code" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block>
                Verify
              </Button>
            </Form.Item>
          </Form>
        )}
      </div>
      {contextHolder}
    </>
  );
};

export default PhoneVerificationPage;
